import { TeliaGrid } from '@teliads/components/react';
import { useTranslation } from 'react-i18next';

import type { PageLevel0Props } from './models';
import './PageLevel0.scss';

import { BaseCard, ButtonVariantEnum, CardVariantEnum } from 'components/custom/base-card';
import Carousel from 'components/custom/carousel/Carousel';
import ContentBannerPurPur from 'components/custom/content-banner-purpur/ContentBannerPurPur';
import {
  LabelValueStatistics,
  LabelValueStatisticsWrapper,
} from 'components/custom/label-value-statistics';
import PreviewList from 'components/custom/preview-list/PreviewList';
import SEO from 'components/custom/SEO/SEO';
import { BillboardPreamble } from 'components/voca/billboard/BillboardPreamble';
import Heading from 'components/voca/heading/Heading';
import HeroBanner from 'components/voca/hero-banner/HeroBanner';
import { renderPlainTextFromRichText, renderRichText } from 'utils/rich-text-render';

export default function PageLevel0Template({
  heroBanner,
  topCarousel,
  statistics,
  topBillboard,
  campaignCards,
  table,
  bottomBillboard,
  seoMetadata,
}: PageLevel0Props) {
  const { t } = useTranslation();

  return (
    <>
      <SEO
        title={seoMetadata.title}
        meta={seoMetadata.meta}
        socialMedia={seoMetadata.socialMedia}
      />

      <HeroBanner
        title={heroBanner.title}
        preamble={
          heroBanner.preamble ? renderPlainTextFromRichText(heroBanner.preamble.json) : <></>
        }
        video={
          heroBanner.videoUrl || heroBanner.image.url
            ? {
                url: heroBanner.videoUrl,
                poster: heroBanner.image.url,
                altText: heroBanner.image.altText,
              }
            : undefined
        }
        cta={heroBanner.cta}
      />

      <TeliaGrid>
        <Heading tag="h2" variant="title-100" className="page-level-0__top-carousel-heading">
          {topCarousel.title}
        </Heading>

        {topCarousel.items.length > 1 && (
          <Carousel>
            {topCarousel.items.map((card, index: number) => (
              <BaseCard
                key={`${card.title}-${index}`}
                cardVariant={CardVariantEnum.LARGE}
                title={card.title}
                imgSrc={card.image.url}
                altText={card.image.altText}
                focusArea={card.image.focusArea}
                link={card.url}
              />
            ))}
          </Carousel>
        )}

        {topCarousel.items.length === 1 && (
          <>
            {topCarousel.items.map((card, index: number) => (
              <BaseCard
                key={`${card.title}-${index}`}
                cardVariant={CardVariantEnum.LARGE}
                title={card.title}
                imgSrc={card.image.url}
                altText={card.image.altText}
                focusArea={card.image.focusArea}
                link={card.url}
              />
            ))}
          </>
        )}

        <LabelValueStatisticsWrapper>
          {statistics.map((factBox, index) => (
            <LabelValueStatistics
              key={`${factBox.title}-${index}`}
              label={factBox.title}
              value={renderPlainTextFromRichText(factBox.body.json)}
            />
          ))}
        </LabelValueStatisticsWrapper>
      </TeliaGrid>

      <div className="page-level-0__billboard-wrapper">
        <ContentBannerPurPur
          badge={t('latest_report')}
          title={topBillboard.title}
          variant="lg"
          imgPosition="right"
          imgSrc={topBillboard.image.url}
          imgAlt={topBillboard.image.altText}
          preamble={
            <BillboardPreamble
              links={[
                {
                  type: 'video',
                  href: topBillboard.bottomLinks[0].href,
                  name: topBillboard.bottomLinks[0].name,
                  openInNewTab: true,
                },
                {
                  type: 'doc',
                  href: topBillboard.bottomLinks[1].href,
                  name: topBillboard.bottomLinks[1].name,
                },
              ]}
            >
              {renderRichText(topBillboard.preamble)}
            </BillboardPreamble>
          }
          cta={{
            name: topBillboard.cta.text,
            href: topBillboard.cta.url,
          }}
        />
      </div>

      <TeliaGrid class="page-level-0__bottom-section-wrapper">
        {campaignCards.length > 1 && (
          <Carousel>
            {campaignCards.map((card, index: number) => (
              <BaseCard
                key={`${card.title}-${index}`}
                cardVariant={CardVariantEnum.LARGE}
                title={card.title}
                imgSrc={card.image.url}
                altText={card.image.altText}
                focusArea={card.image.focusArea}
                text={card.body}
                buttonVariant={ButtonVariantEnum.SECONDARY_WHITE}
                buttonText={card.tagline || t('read_more')}
                link={card.url}
              />
            ))}
          </Carousel>
        )}

        {campaignCards.length === 1 && (
          <>
            {campaignCards.map((card, index: number) => (
              <BaseCard
                key={`${card.title}-${index}`}
                cardVariant={CardVariantEnum.LARGE}
                title={card.title}
                imgSrc={card.image.url}
                altText={card.image.altText}
                focusArea={card.image.focusArea}
                text={card.body}
                buttonVariant={ButtonVariantEnum.SECONDARY_WHITE}
                buttonText={card.tagline || t('read_more')}
                link={card.url}
              />
            ))}
          </>
        )}

        <div className="page-level-0__table-wrapper">
          <PreviewList title={table.title} cta={table.cta} />
        </div>

        <BaseCard
          cardVariant={CardVariantEnum.LARGE}
          title={bottomBillboard.title}
          imgSrc={bottomBillboard.image.url}
          altText={bottomBillboard.image.altText}
          focusArea={bottomBillboard.image.focusArea}
          text={renderRichText(bottomBillboard.preamble)}
          buttonVariant={ButtonVariantEnum.EXPRESSIVE}
          buttonText={bottomBillboard.cta.text}
          link={bottomBillboard.cta.url}
        />
      </TeliaGrid>
    </>
  );
}
